import { environment } from "../config/environment";
import { getFromBackendAPI, postToBackendAPI, patchToBackendAPI, deleteFromBackendAPI } from "./api.service";

/**
 * Status Item DTOs - matching backend API models
 */
export interface StatusItemDto {
  id: number;
  spreadsheetId: string;
  name: string;
  sheetId?: string;
  status?: string;
  type?: string;
  preparerEmail?: string;
  preparerName?: string;
  reviewerEmail?: string;
  reviewerName?: string;
  partnerEmail?: string;
  partnerName?: string;
  createdAt: string;
  lastUpdated: string;
  discussionCount: number;
  documentId?: string;
  latestDocumentVersion?: number;
  isHidden: boolean;
}

export interface CreateStatusItemRequest {
  name: string;
  sheetId?: string;
  status?: string;
  type?: string;
  preparerEmail?: string;
  preparerName?: string;
  reviewerEmail?: string;
  reviewerName?: string;
  partnerEmail?: string;
  partnerName?: string;
}

export interface CreateStatusItemsRequest {
  spreadsheetId: string;
  items: CreateStatusItemRequest[];
}

export interface UpdateStatusRequest {
  status: string;
}

export interface UpdateVisibilityRequest {
  isHidden: boolean;
}

export interface UpdateDocumentRequest {
  documentId: string | null;
}

export interface UpdateNameRequest {
  name: string;
}

/**
 * Get all status items for a given spreadsheet
 * @param spreadsheetId - The spreadsheet ID (GUID)
 * @returns Promise with array of status items including discussion counts
 */
export async function getStatusItemsBySpreadsheet(spreadsheetId: string): Promise<StatusItemDto[]> {
  const endpoint = `${environment.api.baseUrl}${environment.api.endpoints.statusItems}/spreadsheet/${spreadsheetId}`;
  return await getFromBackendAPI<StatusItemDto[]>(endpoint);
}

/**
 * Create one or more status items for a spreadsheet
 * @param request - The spreadsheet ID and array of status items to create
 * @returns Promise with array of created status items
 */
export async function createStatusItems(request: CreateStatusItemsRequest): Promise<StatusItemDto[]> {
  const endpoint = `${environment.api.baseUrl}${environment.api.endpoints.statusItems}/bulk`;
  return await postToBackendAPI<StatusItemDto[]>(endpoint, request);
}

/**
 * Update the status of a status item
 * @param id - The status item ID
 * @param status - The new status value
 * @returns Promise with the updated status item
 */
export async function updateStatusItemStatus(id: number, status: string): Promise<StatusItemDto> {
  const endpoint = `${environment.api.baseUrl}${environment.api.endpoints.statusItems}/${id}/status`;
  const request: UpdateStatusRequest = { status };
  return await patchToBackendAPI<StatusItemDto>(endpoint, request);
}

/**
 * Delete a status item
 * @param id - The status item ID
 * @returns Promise that resolves when deletion is complete
 */
export async function deleteStatusItem(id: number): Promise<void> {
  const endpoint = `${environment.api.baseUrl}${environment.api.endpoints.statusItems}/${id}`;
  return await deleteFromBackendAPI<void>(endpoint);
}

/**
 * Update the name of a status item
 * @param id - The status item ID
 * @param name - The new name
 * @returns Promise with the updated status item
 */
export async function updateStatusItemName(id: number, name: string): Promise<StatusItemDto> {
  const endpoint = `${environment.api.baseUrl}${environment.api.endpoints.statusItems}/${id}/name`;
  const request: UpdateNameRequest = { name };
  return await patchToBackendAPI<StatusItemDto>(endpoint, request);
}

/**
 * Update the visibility of a status item
 * @param id - The status item ID
 * @param isHidden - Whether the item should be hidden
 * @returns Promise with the updated status item
 */
export async function updateStatusItemVisibility(id: number, isHidden: boolean): Promise<StatusItemDto> {
  const endpoint = `${environment.api.baseUrl}${environment.api.endpoints.statusItems}/${id}/visibility`;
  const request: UpdateVisibilityRequest = { isHidden };
  return await patchToBackendAPI<StatusItemDto>(endpoint, request);
}

/**
 * Link or unlink a client document to a status item
 * @param id - The status item ID
 * @param documentId - The client document ID to link, or null to unlink
 * @returns Promise with the updated status item
 */
export async function updateStatusItemDocument(id: number, documentId: string | null): Promise<StatusItemDto> {
  const endpoint = `${environment.api.baseUrl}${environment.api.endpoints.statusItems}/${id}/document`;
  const request: UpdateDocumentRequest = { documentId };
  return await patchToBackendAPI<StatusItemDto>(endpoint, request);
}
